import { scry, scryImageUrl } from "../lib/scryClient";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { auth, db } from "@/lib/firebase";
import { doc, getDoc, serverTimestamp } from "firebase/firestore"; // removed addDoc, collection, setLogLevel
import { Button } from "@/components/ui/button";
import { safeSendOfferV2, ensureOfferCardSnapshot } from "@/data/offersV2"; // add ensureOfferCardSnapshot
import { toast } from "@/components/ui/use-toast";

const OFFER_SOURCE_VERSION = "offer-composer-v2.1";

// (ADD) Root mount helper & global open function (if not already present)
let offerRootEl: HTMLElement | null = null;
function ensureRoot() {
  if (offerRootEl) return offerRootEl;
  const el = document.createElement("div");
  el.id = "offer-composer-root";
  document.body.appendChild(el);
  offerRootEl = el;
  return el;
}

// (ADD) external open hook
export function openOfferComposer(payload: OfferComposerInit) {
  window.dispatchEvent(new CustomEvent("offer:open", { detail: payload }));
}

// Extend init payload to optionally include avatar + pre-known username/display
export type OfferComposerInit = {
  toUid: string;
  toUsername?: string | null;
  toAvatarUrl?: string | null;
  card: OfferComposerCard;
};

export type OfferComposerCard = {
  cardId?: string;
  name: string;
  image?: string | null;
  set?: string | null;
  number?: string | number | null;
  price?: number | null;
  foil?: boolean;
  condition?: string | null;
  status?: "sale" | "trade";
  notes?: string | null;
  scryfall_id?: string | null;
};

// Helper: strip undefined recursively (Firestore rejects undefined)
function deepClean<T>(obj: T): T {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) {
    return obj.map((v) => deepClean(v)).filter((v) => v !== undefined) as any;
  }
  const out: any = {};
  for (const [k, v] of Object.entries(obj as any)) {
    if (v === undefined) continue;
    if (v && typeof v === "object") {
      const cv = deepClean(v);
      if (cv !== undefined) out[k] = cv;
    } else {
      out[k] = v;
    }
  }
  return out;
}

// UPDATE safe payload typing (add fromUid, status)
type SendOfferV2Input = {
  fromUid: string;
  toUid: string;
  offerType: "trade" | "purchase";
  offeredPrice: number | null;
  message: string | null;
  cardId?: string;
  cardOwnerUid: string;
  scryfall_id?: string;
  targetCard: {
    cardId?: string;
    name: string;
    status?: string | null;
    price?: number | null;
    foil?: boolean;
    condition?: string | null;
    set?: string | null;
    number?: string | number | null;
    scryfall_id?: string | null;
    image?: string | null;
    notes?: string | null;
  };
  offeredItems: any[];
  requestedItems: any[];
  status: "open";
  createdAt: any;
  updatedAt: any;
  participants: string[];
  participantSet: Record<string, true>;
  keys: {
    pair: string;
    from_to: string;
    to_from: string;
  };
  fromMeta: {
    username?: string | null;
    avatar_url?: string | null;
  };
  toMeta: {
    username?: string | null;
    avatar_url?: string | null;
  };
  clientMeta: {
    source: string;
    ts: number;
    ua?: string;
    version?: string;
  };
};

export function OfferComposerRoot() {
  const [state, setState] = useState<State>({ open: false, payload: null });
  const [targetUsername, setTargetUsername] = useState("");
  const [targetAvatar, setTargetAvatar] = useState<string | null>(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [offerType, setOfferType] = useState<"trade" | "purchase">("trade");
  const [priceInput, setPriceInput] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [avatarBroken, setAvatarBroken] = useState(false); // NEW fallback flag

  // (REF) for focus trapping & outside click
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Listen open
  useEffect(() => {
    const onOpen = (e: any) => {
      const p: OfferComposerInit = e.detail;
      if (!p) return;
      setState({ open: true, payload: p });
      setErr(null);
      setMessage("");
      setPriceInput(
        p.card.status === "sale" && typeof p.card.price === "number"
          ? p.card.price.toFixed(2)
          : ""
      );
      setOfferType(p.card.status === "sale" ? "purchase" : "trade");
      setTargetUsername(p.toUsername || p.toUid.slice(0, 6));
      setTargetAvatar(p.toAvatarUrl || (p as any).toAvatar || null);
      setAvatarBroken(false); // reset fallback state each open
    };
    window.addEventListener("offer:open", onOpen);
    return () => window.removeEventListener("offer:open", onOpen);
  }, []);

  // Fetch missing profile bits
  useEffect(() => {
    (async () => {
      if (!state.open || !state.payload) return;
      if (state.payload.toUsername && state.payload.toAvatarUrl) return;
      try {
        setLoadingUser(true);
        const snap = await getDoc(doc(db, "users", state.payload.toUid));
        if (snap.exists()) {
          const v: any = snap.data();
          setTargetUsername(
            v?.username ||
              v?.display_name ||
              state.payload.toUsername ||
              state.payload.toUid.slice(0, 6)
          );
          setTargetAvatar(
            v?.avatar_url ||
              v?.photoURL ||
              state.payload.toAvatarUrl ||
              null
          );
        }
      } finally {
        setLoadingUser(false);
      }
    })();
  }, [state.open, state.payload]);

  // Escape
  useEffect(() => {
    if (!state.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "Tab" && dialogRef.current) {
        // simple focus trap
        const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.open]);

  // Scroll lock
  useEffect(() => {
    if (!state.open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [state.open]);

  const close = useCallback(() => {
    setState({ open: false, payload: null });
    setErr(null);
  }, []);

  // --- submit (unchanged logic) ---
  async function submit() {
    if (!state.payload) return;
    const me = auth.currentUser;
    setErr(null);
    if (!me) return setErr("Sign in first.");
    if (me.uid === state.payload.toUid) return setErr("Cannot offer to yourself.");

    let finalPrice: number | null = null;
    if (offerType === "purchase") {
      if (priceInput.trim() === "") {
        const base = state.payload.card.price;
        finalPrice = typeof base === "number" && Number.isFinite(base) ? base : 0;
      } else {
        const n = Number(priceInput);
        if (!Number.isFinite(n) || n < 0) return setErr("Enter a valid amount.");
        finalPrice = Number(n.toFixed(2));
      }
    }

    setSubmitting(true);
    try {
      const card = state.payload.card;
      const toUid = state.payload.toUid;
      const participants = [me.uid, toUid].sort();
      const offerPayload: SendOfferV2Input = {
        fromUid: me.uid,
        toUid,
        offerType,
        offeredPrice: offerType === "purchase" ? finalPrice : null,
        message: (message || "").trim() || null,
        cardId: card.cardId || undefined,
        cardOwnerUid: toUid,
        scryfall_id: card.scryfall_id || undefined,
        targetCard: {
          cardId: card.cardId,
          name: card.name,
          status: card.status || null,
          price:
            card.status === "sale" && typeof card.price === "number"
              ? card.price
              : null,
          foil: !!card.foil,
          condition: card.condition || null,
          set: card.set || null,
          number: card.number ?? null,
          scryfall_id: card.scryfall_id || null,
          image: card.image || null,
          notes: card.notes || null,
        },
        offeredItems: [],
        requestedItems: [],
        status: "open",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        participants,
        participantSet: { [me.uid]: true, [toUid]: true },
        keys: {
          pair: participants.join("__"),
          from_to: `${me.uid}__${toUid}`,
          to_from: `${toUid}__${me.uid}`,
        },
        fromMeta: {
          username: me.displayName || null,
          avatar_url: (me as any).photoURL || null,
        },
        toMeta: {
          username: targetUsername || null,
          avatar_url: targetAvatar || null,
        },
        clientMeta: {
          source: OFFER_SOURCE_VERSION,
          ts: Date.now(),
          ua:
            typeof navigator !== "undefined"
              ? navigator.userAgent
              : undefined,
          version: "1.0.0",
        },
      };

      // Clean then send using imported safeSendOfferV2
      const cleanOffer = deepClean(offerPayload) as SendOfferV2Input;
      const id = await safeSendOfferV2(cleanOffer);

      try {
        if (state.payload.card.cardId) {
          await ensureOfferCardSnapshot({
            id,
            cardRefPath: `users/${state.payload.toUid}/cards/${state.payload.card.cardId}`,
          });
        }
      } catch (e) {
        console.warn("ensureOfferCardSnapshot failed (non-fatal)", e);
      }

      toast({ description: "Offer sent ✅" });
      setTimeout(() => toast({ description: null as any }), 1600);
      close();
    } catch (e: any) {
      console.error("offer submit failed", e);
      setErr(
        /Missing or insufficient permissions/i.test(e?.message || "")
          ? "Permission denied (check Firestore rules for offers_v2)."
          : e?.message || "Could not send offer."
      );
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    (window as any).__offerComposerMounted = true;
  }, []);

  if (!state.open || !state.payload) return null;
  const { card } = state.payload;
  const isSale = card.status === "sale";
  const priceLabel =
    typeof card.price === "number" ? `$${card.price.toFixed(2)}` : "—";
  const root = ensureRoot();

  // Overlay now uses pointer events on wrapper; backdrop click closes
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Compose offer"
      data-offer-composer-root
    >
      {/* Backdrop (click-away closes) */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity animate-fadeIn"
        onMouseDown={close}
      />
      {/* Modal */}
      <div
        ref={dialogRef}
        className="relative w-full max-w-[560px] origin-center scale-100 animate-scaleIn rounded-xl border border-white/15 bg-[#0d1116] shadow-2xl flex flex-col overflow-visible" // allow dropdowns/popovers
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/5 backdrop-blur relative z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative h-9 w-9 rounded-full overflow-hidden border border-white/20 bg-white/10 flex items-center justify-center shrink-0">
              {(targetAvatar && !avatarBroken) ? (
                <img
                  src={targetAvatar}
                  alt={targetUsername || "user"}
                  className="h-full w-full object-cover"
                  draggable={false}
                  onError={() => setAvatarBroken(true)}
                />
              ) : (
                <span className="text-[11px] font-semibold text-white/70">
                  {(targetUsername || "U").slice(0, 2).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[11px] uppercase tracking-wide text-white/40">
                  Offer to
                </span>
                <span className="text-sm font-semibold text-white truncate max-w-[180px]">
                  @{(targetUsername || state.payload.toUid.slice(0, 6)).replace(/^@/, "")}
                </span>
                {loadingUser && (
                  <span className="text-[10px] text-white/40">…</span>
                )}
              </div>
              {avatarBroken && (
                <span className="text-[10px] text-amber-300/70 mt-0.5">
                  Avatar unavailable – placeholder shown
                </span>
              )}
            </div>
          </div>
          <button
            onClick={close}
            className="rounded-md px-2 py-1 text-xs bg-white/10 text-white/70 hover:bg-white/20"
          >
            Cancel
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-4 p-4 overflow-y-auto max-h-[70vh]">
          <div className="flex gap-4">
            <div className="relative w-[120px] shrink-0 z-0">
              <div className="aspect-[63/88] w-full rounded-md overflow-hidden ring-1 ring-white/15 bg-black/40">
                <img
                  src={
                    card.image ||
                    (card.scryfall_id
                      ? ` + scryImageUrl(
                          card.scryfall_id
                        , "normal") + `
                      : "/images/sg-card-back.png")
                  }
                  alt={card.name}
                  className="h-full w-full object-cover"
                  draggable={false}
                />
              </div>
              {card.foil && (
                <div className="absolute top-1 left-1 rounded bg-amber-500/30 px-1.5 py-0.5 text-[10px] font-medium text-amber-100 backdrop-blur">
                  Foil
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 space-y-2 text-sm text-white/80">
              <div className="font-semibold text-white truncate">
                {card.name}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-white/60">
                {card.set && <span>Set: {(card.set || "").toUpperCase()}</span>}
                {card.number && <span>No: {card.number}</span>}
                <span>Status: {card.status || "—"}</span>
                <span>Price: {priceLabel}</span>
                {card.condition && <span>Cond: {card.condition}</span>}
              </div>
              {card.notes && (
                <div className="text-[11px] text-white/55 line-clamp-3">
                  {card.notes}
                </div>
              )}
            </div>
          </div>

          {/* Offer type */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOfferType("trade")}
              className={
                "px-3 py-1 text-xs rounded-md border transition " +
                (offerType === "trade"
                  ? "bg-white/15 border-white/30 text-white"
                  : "bg-black/40 border-white/15 text-white/60 hover:bg-white/10")
              }
            >
              Trade
            </button>
            <button
              type="button"
              disabled={!isSale}
              onClick={() => isSale && setOfferType("purchase")}
              className={
                "px-3 py-1 text-xs rounded-md border transition " +
                (offerType === "purchase"
                  ? "bg-emerald-500/25 border-emerald-400/40 text-emerald-200"
                  : isSale
                  ? "bg-black/40 border-white/15 text-white/60 hover:bg-white/10"
                  : "bg-black/20 border-white/10 text-white/25 cursor-not-allowed")
              }
            >
              Purchase
            </button>
          </div>

          {offerType === "purchase" && (
            <label className="block">
              <span className="block text-[11px] mb-1 text-white/60">
                Offer Amount (USD)
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                className="w-full rounded-md bg-black/40 border border-white/15 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-emerald-400/30"
                placeholder={
                  typeof card.price === "number"
                    ? card.price.toFixed(2)
                    : "0.00"
                }
              />
            </label>
          )}

          <label className="block">
            <span className="block text-[11px] mb-1 text-white/60">
              Message (optional)
            </span>
            <textarea
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hi! I'm interested in this card..."
              className="w-full resize-none rounded-md bg-black/40 border border-white/15 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
            />
          </label>

          {err && (
            <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-400/30 rounded px-2 py-1">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-white/10 bg-white/5">
          <Button
            variant="ghost"
            size="sm"
            onClick={close}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => void submit()}
            disabled={submitting}
          >
            {submitting ? "Sending…" : "Send Offer"}
          </Button>
        </div>
      </div>
    </div>,
    root
  );
}

// (ADD) basic animations (optional – include in global CSS if not present)
// .animate-fadeIn { animation: fadeIn .18s ease; }
// .animate-scaleIn { animation: scaleIn .18s ease; }
// @keyframes fadeIn { from {opacity:0} to {opacity:1} }
// @keyframes scaleIn { from {opacity:0; transform:scale(.94)} to {opacity:1; transform:scale(1)} }

